import { type Level, LEVELS, PUZZLE_TYPES, type PuzzleShape, type PuzzleType } from '@arrow/puzzle-schema';

import { addDays, type DateKey, daysBetween, isDateKey, type PlayerStats, weekday } from './daily.ts';

/**
 * Play: the archive of past dailies (and the practice pool) as one list of items, the
 * filters and stacks over it, and the search box's reading of what was typed
 * (docs/mockups/play-2.html, Direction F "Stacks" with D's search box).
 *
 * Pure: today, the player's first day and their saved progress are passed in. Nothing
 * here reads a clock, storage or the network. Today's puzzle and anything later never
 * become items: Play is the archive; today lives on Today.
 */

// ---------------------------------------------------------------------------------------
// The data
// ---------------------------------------------------------------------------------------

/** One level of a past day, as `GET /v1/archive` lists it: never a clue or an answer. */
export interface ArchiveLevel {
	level: Level;
	difficulty: number;
	width: number;
	height: number;
	puzzleId: string;
	version: number;
	shape: PuzzleShape;
	/** A special puzzle's type (Halloween…); absent on an ordinary daily. */
	type?: PuzzleType;
}

export interface ArchiveDay {
	date: DateKey;
	number: number;
	levels: ArchiveLevel[];
}

/** The first and latest released daily. */
export interface ArchiveBound {
	date: DateKey;
	number: number;
}

/** A practice puzzle, as `GET /v1/practice` lists it. */
export interface PracticeEntry {
	id: string;
	title: string;
	level: Level;
	difficulty: number;
	width: number;
	height: number;
	shape: PuzzleShape;
}

/** What the device knows of one game from its save (the web's resume summary). */
export interface LocalProgress {
	status: 'new' | 'started' | 'completed';
	elapsedMs: number;
	answersSolved: number;
	answersTotal: number | null;
}

export interface ArchiveLocal {
	stats: PlayerStats;
	/** Saved progress, keyed by {@link dailyKey} or {@link practiceKey}. */
	progress: Readonly<Record<string, LocalProgress>>;
}

/** The key of a daily item (and of its saved progress): `2026-10-17:hard`. */
export const dailyKey = (date: DateKey, level: Level): string => `${date}:${level}`;
/** The key of a practice item: `practice:<id>`. */
export const practiceKey = (id: string): string => `practice:${id}`;

export type ItemStatus = 'new' | 'started' | 'done';

/** One puzzle you could play from Play. */
export interface ArchiveItem {
	kind: 'daily' | 'practice';
	/** {@link dailyKey} or {@link practiceKey}. */
	key: string;
	/** The daily's date and number (null for practice). */
	date: DateKey | null;
	number: number | null;
	/** A practice puzzle's title (null for a daily). */
	title: string | null;
	puzzleId: string;
	level: Level;
	difficulty: number;
	width: number;
	height: number;
	/** The longer side: what "small" and "big" go by. */
	size: number;
	shape: PuzzleShape;
	/** A special puzzle's type (null for an ordinary one): what puts it in a special collection. */
	type: PuzzleType | null;
	/** 0 = Monday … 6 = Sunday (null for practice). */
	weekday: number | null;
	/** Whole days before today (1 = yesterday; null for practice). */
	daysAgo: number | null;
	status: ItemStatus;
	/** Done with no checks and no reveals. */
	perfect: boolean;
	/** In progress: the share of answers solved (0–1), when the save knows the total. */
	pct: number | null;
	/** Solving time so far (started) or the solve's time (done); 0 when new. */
	elapsedMs: number;
	/** Done on its own day (true), caught up later (false), or not known / not done (null). */
	onTime: boolean | null;
	/** Released since your first day, before today, and not finished. */
	missed: boolean;
}

const sizeOf = (w: number, h: number) => Math.max(w, h);

/**
 * Every past daily as items, newest first (easy → hard within a day), with your state
 * on each. Days from today on are left out. `firstSeenKey` is your first day on this
 * device: a day from then until yesterday you haven't finished is `missed`.
 */
export function archiveItems(
	days: readonly ArchiveDay[],
	local: ArchiveLocal,
	todayKey: DateKey,
	firstSeenKey: DateKey | null,
): ArchiveItem[] {
	if (!isDateKey(todayKey)) return [];
	const since = firstSeenKey && isDateKey(firstSeenKey) ? firstSeenKey : null;
	const out: ArchiveItem[] = [];
	const sorted = [...days].filter((d) => isDateKey(d.date) && d.date < todayKey).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
	for (const day of sorted) {
		const levels = [...day.levels].sort((a, b) => LEVELS.indexOf(a.level) - LEVELS.indexOf(b.level));
		for (const lv of levels) {
			const key = dailyKey(day.date, lv.level);
			const found = local.stats.days[day.date]?.[lv.level];
			// A record for another puzzle at this slot isn't this puzzle's.
			const record = found && found.puzzleId === lv.puzzleId ? found : undefined;
			const saved = local.progress[key];
			const done = record?.completedAt != null || saved?.status === 'completed';
			const started = !done && (saved?.status === 'started' || record !== undefined);
			const status: ItemStatus = done ? 'done' : started ? 'started' : 'new';
			const elapsedMs = done && record?.completedAt != null ? record.elapsedMs : (saved?.elapsedMs ?? record?.elapsedMs ?? 0);
			out.push({
				kind: 'daily',
				key,
				date: day.date,
				number: day.number,
				title: null,
				puzzleId: lv.puzzleId,
				level: lv.level,
				difficulty: lv.difficulty,
				width: lv.width,
				height: lv.height,
				size: sizeOf(lv.width, lv.height),
				shape: lv.shape,
				type: lv.type && (PUZZLE_TYPES as readonly string[]).includes(lv.type) ? lv.type : null,
				weekday: weekday(day.date),
				daysAgo: daysBetween(day.date, todayKey),
				status,
				perfect: done && record?.completedAt != null && record.checksUsed === 0 && record.revealsUsed === 0,
				pct: status === 'started' ? progressShare(saved) : null,
				elapsedMs: status === 'new' ? 0 : elapsedMs,
				onTime: done && record?.completedAt != null ? record.completedOnDay === day.date : null,
				missed: !done && since !== null && day.date >= since,
			});
		}
	}
	return out;
}

function progressShare(saved: LocalProgress | undefined): number | null {
	if (!saved || !saved.answersTotal) return null;
	return Math.min(1, Math.max(0, saved.answersSolved / saved.answersTotal));
}

/** The practice pool as items, in the pool's own order (easiest first). */
export function practiceItems(list: readonly PracticeEntry[], local: ArchiveLocal): ArchiveItem[] {
	return list.map((p) => {
		const key = practiceKey(p.id);
		const saved = local.progress[key];
		const status: ItemStatus = saved?.status === 'completed' ? 'done' : saved?.status === 'started' ? 'started' : 'new';
		return {
			kind: 'practice',
			key,
			date: null,
			number: null,
			title: p.title,
			puzzleId: p.id,
			level: p.level,
			difficulty: p.difficulty,
			width: p.width,
			height: p.height,
			size: sizeOf(p.width, p.height),
			shape: p.shape,
			type: null,
			weekday: null,
			daysAgo: null,
			status,
			perfect: false,
			pct: status === 'started' ? progressShare(saved) : null,
			elapsedMs: status === 'new' ? 0 : (saved?.elapsedMs ?? 0),
			onTime: null,
			missed: false,
		};
	});
}

// ---------------------------------------------------------------------------------------
// Filters and sorting
// ---------------------------------------------------------------------------------------

export type StatusKey = 'new' | 'started' | 'done' | 'perfect' | 'missed';
export const STATUS_KEYS: readonly StatusKey[] = ['new', 'started', 'done', 'perfect', 'missed'];
/** Small: up to 8×8. Medium: 9×9 and 10×10. Big: 11×11 and up (Sunday's 13×13). */
export type SizeKey = 'any' | 'small' | 'medium' | 'big';
export type RangeKey = 'all' | '7' | '30' | '90';
export type SortKey = 'new' | 'old' | 'quick' | 'big';
export const SORT_KEYS: readonly SortKey[] = ['new', 'old', 'quick', 'big'];

export interface ArchiveFilter {
	/** Any of these levels (none = any level). */
	levels: Level[];
	/** Any of these states (none = any state). */
	status: StatusKey[];
	size: SizeKey;
	/** Out within the last n days (`all` = back to No. 1). */
	range: RangeKey;
	/** 0 = Monday … 6 = Sunday. */
	weekday: number | null;
	/** `month` is 1–12. */
	month: { year: number; month: number } | null;
	sort: SortKey;
}

/** A filter key a loosening (and a removable chip) works on. */
export type FilterKey = 'levels' | 'status' | 'size' | 'range' | 'weekday' | 'month';
export const FILTER_KEYS: readonly FilterKey[] = ['levels', 'status', 'size', 'range', 'weekday', 'month'];

export const NO_FILTER: ArchiveFilter = Object.freeze({ levels: [], status: [], size: 'any', range: 'all', weekday: null, month: null, sort: 'new' }) as ArchiveFilter;

export function makeFilter(partial: Partial<ArchiveFilter> = {}): ArchiveFilter {
	return { ...NO_FILTER, levels: [], status: [], ...partial };
}

/** The filter with one key back to "any". */
export function withoutKey(filter: ArchiveFilter, key: FilterKey): ArchiveFilter {
	const blank = makeFilter();
	return { ...filter, [key]: blank[key] };
}

/** Any filter at all (sort aside). */
export function isFiltered(f: ArchiveFilter): boolean {
	return f.levels.length > 0 || f.status.length > 0 || f.size !== 'any' || f.range !== 'all' || f.weekday !== null || f.month !== null;
}

export function sizeKeyOf(size: number): Exclude<SizeKey, 'any'> {
	return size <= 8 ? 'small' : size <= 10 ? 'medium' : 'big';
}

/** The states an item answers to (an in-progress missed day is both). */
export function statusKeys(item: ArchiveItem): StatusKey[] {
	const k: StatusKey[] = [item.status];
	if (item.perfect) k.push('perfect');
	if (item.missed) k.push('missed');
	return k;
}

export function matchFilter(item: ArchiveItem, f: ArchiveFilter): boolean {
	if (f.levels.length && !f.levels.includes(item.level)) return false;
	if (f.size !== 'any' && sizeKeyOf(item.size) !== f.size) return false;
	if (f.weekday !== null && item.weekday !== f.weekday) return false;
	if (f.month !== null) {
		if (!item.date) return false;
		if (Number(item.date.slice(0, 4)) !== f.month.year || Number(item.date.slice(5, 7)) !== f.month.month) return false;
	}
	if (f.range !== 'all' && (item.daysAgo === null || item.daysAgo > Number(f.range))) return false;
	if (f.status.length && !statusKeys(item).some((k) => f.status.includes(k))) return false;
	return true;
}

const levelRank = (l: Level) => LEVELS.indexOf(l);
const byDate = (a: ArchiveItem, b: ArchiveItem) => (a.date ?? '').localeCompare(b.date ?? '');

/** Sorted copy. Newest / oldest first by date (easy → hard on a day); smallest or biggest boards first. */
export function sortItems(items: readonly ArchiveItem[], sort: SortKey): ArchiveItem[] {
	const out = [...items];
	switch (sort) {
		case 'new':
			return out.sort((a, b) => byDate(b, a) || levelRank(a.level) - levelRank(b.level));
		case 'old':
			return out.sort((a, b) => byDate(a, b) || levelRank(a.level) - levelRank(b.level));
		case 'quick':
			return out.sort((a, b) => a.size - b.size || levelRank(a.level) - levelRank(b.level) || byDate(b, a));
		case 'big':
			return out.sort((a, b) => b.size - a.size || levelRank(b.level) - levelRank(a.level) || byDate(b, a));
	}
}

/** The items a filter keeps, in its sort order. */
export function applyFilter(items: readonly ArchiveItem[], f: ArchiveFilter): ArchiveItem[] {
	return sortItems(
		items.filter((it) => matchFilter(it, f)),
		f.sort,
	);
}

export interface Relaxation {
	key: FilterKey;
	/** How many more puzzles dropping it would show. */
	adds: number;
}

/**
 * No results is a way back: the loosenings (one filter dropped) that bring puzzles back,
 * the nearest first (fewest added), at most `max`.
 */
export function relaxations(items: readonly ArchiveItem[], f: ArchiveFilter, max = 3): Relaxation[] {
	const base = items.filter((it) => matchFilter(it, f)).length;
	const out: Relaxation[] = [];
	for (const key of FILTER_KEYS) {
		const g = withoutKey(f, key);
		if (JSON.stringify(g) === JSON.stringify(f)) continue;
		const adds = items.filter((it) => matchFilter(it, g)).length - base;
		if (adds > 0) out.push({ key, adds });
	}
	return out.sort((a, b) => a.adds - b.adds || FILTER_KEYS.indexOf(a.key) - FILTER_KEYS.indexOf(b.key)).slice(0, max);
}

// ---------------------------------------------------------------------------------------
// Stacks: saved filters you can play from
// ---------------------------------------------------------------------------------------

/** A built-in stack's id. A special's id is its puzzle type. */
export type StackId = 'progress' | 'missed' | 'start' | 'quick' | 'sundays' | 'everything' | 'practice' | PuzzleType;

export interface StackDef {
	/** A built-in's {@link StackId}, or a player-made stack's `my-…` id. */
	id: string;
	name: string;
	/** One line under the name. */
	sub: string;
	/** Past dailies or the practice pool. */
	source: 'daily' | 'practice';
	filter: ArchiveFilter;
	/** Shown only to a new player (first day) or only to a returning one. */
	only?: 'new' | 'returning';
	/** Made by the player (saved on this device, see {@link SavedStack}). */
	mine?: boolean;
	/** A special collection we made: only puzzles of this type, before the filter. */
	special?: PuzzleType;
}

/**
 * The built-in stacks, as data. A stack is a filter with a name: a player-made stack is
 * one more `StackDef` ({@link savedStackDef}) appended to {@link stacksFor}'s list, and
 * everything downstream (counts, covers, ▶, the open stack, next in stack) works from the def.
 */
export const STACKS: readonly StackDef[] = [
	{ id: 'progress', name: 'In progress', sub: 'Started, not finished', source: 'daily', filter: makeFilter({ status: ['started'] }) },
	{ id: 'missed', name: 'Missed', sub: 'Not finished on the day', source: 'daily', filter: makeFilter({ status: ['missed'] }), only: 'returning' },
	{
		id: 'quick',
		name: 'Quick ones',
		sub: 'Easy and Medium, up\u00a0to\u00a08×8',
		source: 'daily',
		filter: makeFilter({ levels: ['easy', 'medium'], size: 'small', status: ['new', 'started'], sort: 'quick' }),
	},
	{ id: 'sundays', name: 'Sundays', sub: 'The toughest of each week', source: 'daily', filter: makeFilter({ weekday: 6, status: ['new', 'started'] }) },
	{ id: 'everything', name: 'Everything', sub: '', source: 'daily', filter: makeFilter() },
	{ id: 'practice', name: 'Practice', sub: 'Extra puzzles, no streak', source: 'practice', filter: makeFilter({ sort: 'quick' }) },
];

/**
 * The special collections: the puzzles of one type (seasonal sets…), dressed in that type's
 * look on Play. Listed first, and like every built-in hidden while empty, so one appears
 * when its first puzzle is in the archive.
 */
export const SPECIALS: readonly StackDef[] = [
	{ id: 'halloween', name: 'Halloween', sub: 'Three spooky Arrowwords, from 31 October', source: 'daily', filter: makeFilter({ sort: 'quick' }), special: 'halloween' },
];

/** A new player is on their first day: nothing can be missed yet. */
export function isNewPlayer(todayKey: DateKey, firstSeenKey: DateKey | null): boolean {
	return !firstSeenKey || !isDateKey(firstSeenKey) || firstSeenKey >= todayKey;
}

/**
 * The stacks this player sees, in order: Missed only for a
 * returning one, then the player's own (`mine`, from {@link savedStackDef}).
 */
export function stacksFor(newPlayer: boolean, mine: readonly StackDef[] = []): StackDef[] {
	return [...SPECIALS, ...STACKS.filter((s) => !s.only || s.only === (newPlayer ? 'new' : 'returning')), ...mine];
}

/** A built-in stack, or one of the player's own (`mine`), by id. */
export function findStack(id: string | null | undefined, mine: readonly StackDef[] = []): StackDef | null {
	return SPECIALS.find((s) => s.id === id) ?? STACKS.find((s) => s.id === id) ?? mine.find((s) => s.id === id) ?? null;
}

/** A stack's puzzles, in its order (or `sort`, when the player picked another). */
export function stackItems(stack: StackDef, dailies: readonly ArchiveItem[], practice: readonly ArchiveItem[], sort?: SortKey): ArchiveItem[] {
	const pool = stack.source === 'practice' ? practice : dailies;
	const items = stack.special ? pool.filter((it) => it.type === stack.special) : pool;
	return applyFilter(items, sort ? { ...stack.filter, sort } : stack.filter);
}

/**
 * What to play next from a list in its order: the first unfinished puzzle after
 * `currentKey` (wrapping round to the start), never `currentKey` itself. With no
 * current, the top unfinished one: what ▶ on a stack plays. Null when everything's done.
 */
export function nextInStack(items: readonly ArchiveItem[], currentKey: string | null = null): ArchiveItem | null {
	const at = currentKey === null ? -1 : items.findIndex((it) => it.key === currentKey);
	const n = items.length;
	for (let i = 1; i <= n; i++) {
		const it = items[(at + i + n) % n];
		if (it && it.key !== currentKey && it.status !== 'done') return it;
	}
	return null;
}

// ---------------------------------------------------------------------------------------
// Player-made stacks: a name and filter rules, saved on the device
// ---------------------------------------------------------------------------------------

/**
 * A stack the player made ("New stack" on Play). Only a name and filter rules: nothing
 * about the player. The web app keeps a list of these in storage (`arrow:v1:stacks`).
 */
export interface SavedStack {
	/** `my-` and a few letters or digits: never one of the built-in ids. */
	id: string;
	name: string;
	filter: ArchiveFilter;
}

/** How many stacks a player can keep. */
export const MAX_SAVED_STACKS = 12;
/** The longest name a stack keeps. */
export const STACK_NAME_MAX = 40;
const SAVED_ID = /^my-[a-z0-9]{1,24}$/;
const RANGES: readonly RangeKey[] = ['all', '7', '30', '90'];
const SIZES: readonly SizeKey[] = ['any', 'small', 'medium', 'big'];

/**
 * A filter read from anywhere untrusted (storage, an old build): every rule checked, odd
 * values dropped, duplicates removed, lists in their usual order. Null when it isn't an
 * object at all.
 */
export function normaliseFilter(raw: unknown): ArchiveFilter | null {
	if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
	const o = raw as Record<string, unknown>;
	const list = <T extends string>(v: unknown, allowed: readonly T[]): T[] =>
		Array.isArray(v) ? allowed.filter((a) => v.includes(a)) : [];
	const m = o.month as Record<string, unknown> | null | undefined;
	const month =
		typeof m === 'object' && m !== null && Number.isInteger(m.year) && Number.isInteger(m.month) && (m.year as number) >= 2000 && (m.year as number) <= 2999 && (m.month as number) >= 1 && (m.month as number) <= 12
			? { year: m.year as number, month: m.month as number }
			: null;
	const weekday = Number.isInteger(o.weekday) && (o.weekday as number) >= 0 && (o.weekday as number) <= 6 ? (o.weekday as number) : null;
	return makeFilter({
		levels: list(o.levels, LEVELS),
		status: list(o.status, STATUS_KEYS),
		size: SIZES.includes(o.size as SizeKey) ? (o.size as SizeKey) : 'any',
		// A month and a range are one control ("When"): a month wins.
		range: !month && RANGES.includes(o.range as RangeKey) ? (o.range as RangeKey) : 'all',
		weekday,
		month,
		sort: SORT_KEYS.includes(o.sort as SortKey) ? (o.sort as SortKey) : 'new',
	});
}

const WEEKDAY_PLURAL = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const LEVEL_TITLE: Record<Level, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

/** A filter's rules as short words, in reading order: `["Hard", "not started", "last 90 days"]`. */
export function filterWords(f: ArchiveFilter): string[] {
	const out: string[] = [];
	if (f.levels.length && f.levels.length < LEVELS.length) out.push(f.levels.map((l) => LEVEL_TITLE[l]).join(' and '));
	if (f.weekday !== null) out.push(WEEKDAY_PLURAL[f.weekday]!);
	if (f.size !== 'any') out.push(SIZE_DIMENSIONS[f.size]);
	const st = [...f.status].sort((a, b) => STATUS_KEYS.indexOf(a) - STATUS_KEYS.indexOf(b));
	if (st.length === 2 && st[0] === 'new' && st[1] === 'started') out.push('unfinished');
	else if (st.length) out.push(st.map((k) => STATUS_WORD[k]).join(' or '));
	if (f.month) out.push(`${MONTH_NAMES[f.month.month - 1]} ${f.month.year}`);
	else if (f.range !== 'all') out.push(`last ${f.range} days`);
	return out;
}
const STATUS_WORD: Record<StatusKey, string> = { new: 'not started', started: 'in progress', done: 'done', perfect: 'perfect', missed: 'missed' };
/** Sizes as dimensions, never "small" or "big" on their own (what counts as small isn't obvious). */
const SIZE_DIMENSIONS: Record<Exclude<SizeKey, 'any'>, string> = { small: 'up to 8×8', medium: '9×9 and 10×10', big: '11×11 and up' };

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A stack's name from its rules ("Hard, not started, last 90 days"), for the Name field to start with. */
export function defaultStackName(f: ArchiveFilter): string {
	const words = filterWords(f);
	if (!words.length) return 'Every puzzle';
	let name = '';
	for (const w of words) {
		const next = name ? `${name}, ${w}` : w;
		if (next.length > STACK_NAME_MAX) break;
		name = next;
	}
	return capital(name || words[0]!.slice(0, STACK_NAME_MAX));
}

/** A name as typed, tidied: no control characters, single spaces, at most {@link STACK_NAME_MAX}. Empty: the default. */
export function cleanStackName(raw: unknown, f: ArchiveFilter): string {
	const s = typeof raw === 'string' ? raw.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, STACK_NAME_MAX).trim() : '';
	return s || defaultStackName(f);
}

/** One saved stack, checked; null when it can't be read. */
function readSavedStack(raw: unknown): SavedStack | null {
	if (typeof raw !== 'object' || raw === null) return null;
	const o = raw as Record<string, unknown>;
	if (typeof o.id !== 'string' || !SAVED_ID.test(o.id)) return null;
	const filter = normaliseFilter(o.filter);
	if (!filter) return null;
	return { id: o.id, name: cleanStackName(o.name, filter), filter };
}

/**
 * The player's stacks from storage (`{ v: 1, items }`). Anything odd (another version, not
 * JSON-shaped, a bad item) is left out, never thrown on: a bad blob means no stacks of
 * your own, and the built-ins still work. Ids are unique; at most {@link MAX_SAVED_STACKS}.
 */
export function parseSavedStacks(raw: unknown): SavedStack[] {
	if (typeof raw !== 'object' || raw === null) return [];
	const o = raw as Record<string, unknown>;
	if (o.v !== 1 || !Array.isArray(o.items)) return [];
	const out: SavedStack[] = [];
	for (const item of o.items) {
		const s = readSavedStack(item);
		if (s && !out.some((x) => x.id === s.id)) out.push(s);
		if (out.length >= MAX_SAVED_STACKS) break;
	}
	return out;
}

/** What storage keeps. */
export function serialiseSavedStacks(list: readonly SavedStack[]): { v: 1; items: SavedStack[] } {
	return { v: 1, items: list.slice(0, MAX_SAVED_STACKS).map((s) => ({ id: s.id, name: s.name, filter: s.filter })) };
}

/**
 * A fresh id for a new stack, from `seed` (the caller's clock or random number, so this
 * stays pure), never one in `taken`.
 */
export function newStackId(taken: readonly string[], seed: number): string {
	let n = Math.abs(Math.floor(seed)) % 2_176_782_336; // 36^6
	for (;;) {
		const id = `my-${n.toString(36)}`;
		if (!taken.includes(id)) return id;
		n = (n + 1) % 2_176_782_336;
	}
}

/** A saved stack as a {@link StackDef}: its line is its rules, unless the name already says them. */
export function savedStackDef(s: SavedStack): StackDef {
	const words = capital(filterWords(s.filter).join(', '));
	return {
		id: s.id,
		name: s.name,
		sub: words && words.toLowerCase() !== s.name.toLowerCase() ? words : '',
		source: 'daily',
		filter: s.filter,
		mine: true,
	};
}

/** The rules a stack uses, by name (`stack_saved.rules`): never what they're set to. */
export function filterRuleNames(f: ArchiveFilter): Array<FilterKey | 'sort'> {
	const out: Array<FilterKey | 'sort'> = FILTER_KEYS.filter((k) => JSON.stringify(withoutKey(f, k)) !== JSON.stringify(f));
	if (f.sort !== NO_FILTER.sort) out.push('sort');
	return out;
}

// ---------------------------------------------------------------------------------------
// The search box: numbers and dates go to a day; words become filters
// ---------------------------------------------------------------------------------------

export interface QueryWord {
	key: FilterKey | 'sort';
	value: string;
	/** The words typed for it, as typed (lower case), so its chip can take them out again. */
	source: string;
}

export type ParsedQuery =
	| { kind: 'empty' }
	| { kind: 'number'; number: number; level: Level | null }
	| {
			kind: 'date';
			/** The latest such date up to today (or the one named, with a year). */
			date: DateKey;
			/** Whether the year was typed. */
			yearGiven: boolean;
			level: Level | null;
	  }
	| { kind: 'words'; filter: ArchiveFilter; words: QueryWord[]; unknown: string[] };

const MONTHS: Record<string, number> = {
	jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
	jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};
const WEEKDAYS: Record<string, number> = {
	mon: 0, monday: 0, mondays: 0, tue: 1, tues: 1, tuesday: 1, tuesdays: 1, wed: 2, wednesday: 2, wednesdays: 2,
	thu: 3, thur: 3, thurs: 3, thursday: 3, thursdays: 3, fri: 4, friday: 4, fridays: 4, sat: 5, saturday: 5, saturdays: 5,
	sun: 6, sunday: 6, sundays: 6,
};
const STATUS_WORDS: Record<string, StatusKey> = {
	missed: 'missed', started: 'started', unfinished: 'started', progress: 'started', done: 'done', solved: 'done',
	finished: 'done', perfect: 'perfect', new: 'new', unplayed: 'new', unstarted: 'new', fresh: 'new',
};
const SIZE_WORDS: Record<string, Exclude<SizeKey, 'any'>> = {
	small: 'small', smaller: 'small', little: 'small', quick: 'small', short: 'small', mini: 'small',
	big: 'big', bigger: 'big', large: 'big', huge: 'big',
};
const SORT_WORDS: Record<string, SortKey> = { oldest: 'old', newest: 'new', latest: 'new', smallest: 'quick', quickest: 'quick', biggest: 'big', largest: 'big' };
const FILL = new Set([
	'the', 'a', 'an', 'in', 'on', 'of', 'from', 'and', 'or', 'ones', 'one', 'puzzles', 'puzzle', 'sparrow', 'sparrows', 'level',
	'grid', 'grids', 'boards', 'board', 'only', 'i', 'my', 'me', 'to', 'do', 'yet', 'no', 'no.', '#', 'all', 'any', 'show', 'with',
	'days', 'day', 'ones', 'size', 'that', 'which', 'were', 'was', 'i’ve', "i've",
]);
const LEVEL_WORDS: Record<string, Level> = { easy: 'easy', medium: 'medium', hard: 'hard' };
const DAY_SUFFIX = '(?:st|nd|rd|th)?';
const WD_PREFIX = '(?:(?:mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)[a-z]*\\s+)?';

const pad2 = (n: number) => String(n).padStart(2, '0');

function realDate(y: number, m: number, d: number): DateKey | null {
	const key = `${String(y).padStart(4, '0')}-${pad2(m)}-${pad2(d)}`;
	return isDateKey(key) ? key : null;
}

/** The latest `d m` on or before today (a 29 February looks back to the last leap year). */
function latestDate(d: number, m: number, todayKey: DateKey): DateKey | null {
	const y = Number(todayKey.slice(0, 4));
	for (let year = y; year >= y - 8; year--) {
		const key = realDate(year, m, d);
		if (key && key <= todayKey) return key;
	}
	return null;
}

function yearOf(raw: string | undefined): number | null {
	if (!raw) return null;
	const n = Number(raw);
	return raw.length === 2 ? 2000 + n : n;
}

/** The date in `rest`, if `rest` is just a date: "17 Oct", "Sat 17 Oct", "Oct 17", "17/10", "17/10/2026", "2026-10-17". */
function parseDate(rest: string, todayKey: DateKey): { date: DateKey; yearGiven: boolean } | null {
	let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(rest);
	if (m) {
		const date = realDate(Number(m[1]), Number(m[2]), Number(m[3]));
		return date ? { date, yearGiven: true } : null;
	}
	let day: number | null = null;
	let month: number | null = null;
	let year: number | null = null;
	m = /^(\d{1,2})[/.](\d{1,2})(?:[/.](\d{4}|\d{2}))?$/.exec(rest);
	if (m) [day, month, year] = [Number(m[1]), Number(m[2]), yearOf(m[3])];
	m ??= new RegExp(`^${WD_PREFIX}(\\d{1,2})${DAY_SUFFIX}\\s+([a-z]+)(?:\\s+(\\d{4}))?$`).exec(rest);
	if (m && day === null) {
		const mo = MONTHS[m[2]!];
		if (mo === undefined) return null;
		[day, month, year] = [Number(m[1]), mo, yearOf(m[3])];
	}
	if (day === null) {
		const w = new RegExp(`^${WD_PREFIX}([a-z]+)\\s+(\\d{1,2})${DAY_SUFFIX}(?:\\s+(\\d{4}))?$`).exec(rest);
		const mo = w ? MONTHS[w[1]!] : undefined;
		if (!w || mo === undefined) return null;
		[day, month, year] = [Number(w[2]), mo, yearOf(w[3])];
	}
	if (month === null || month < 1 || month > 12 || day < 1 || day > 31) return null;
	if (year !== null) {
		const date = realDate(year, month, day);
		return date ? { date, yearGiven: true } : null;
	}
	const date = latestDate(day, month, todayKey);
	return date ? { date, yearGiven: false } : null;
}

/** The latest `month` on or before this one. */
function latestMonth(month: number, todayKey: DateKey): number {
	const y = Number(todayKey.slice(0, 4));
	return month <= Number(todayKey.slice(5, 7)) ? y : y - 1;
}

/**
 * Reads the search box (en-GB). A number ("17", "No. 17", "#17") or a date ("17 Oct",
 * "Sat 17 Oct", "17/10") goes to that day, a level word alongside picking its level;
 * anything else is words: level, status (missed, started, done, perfect, new), weekday,
 * month (with a year, or the latest one), size (small, big, 8x8), "last week" / "last
 * month", and a sort word (oldest, biggest). Words it doesn't know are reported in
 * `unknown`, never guessed at.
 */
export function parseQuery(text: string, todayKey: DateKey): ParsedQuery {
	const s = text
		.toLowerCase()
		.replace(/[,·]/g, ' ')
		.replace(/[‘’]/g, "'")
		.replace(/\s+/g, ' ')
		.trim();
	if (!s || !isDateKey(todayKey)) return { kind: 'empty' };

	// A level named beside a number or a date picks that level of the day.
	const tokens = s.split(' ');
	const levels = tokens.filter((t) => LEVEL_WORDS[t]);
	const rest = tokens
		.filter((t) => !LEVEL_WORDS[t])
		.join(' ')
		.replace(/^the\s+/, '');
	const level = levels.length === 1 ? LEVEL_WORDS[levels[0]!]! : null;
	if (levels.length <= 1) {
		const num = /^(?:(?:no\.?|number|num|#)\s*)?(\d{1,6})$/.exec(rest);
		if (num) return { kind: 'number', number: Number(num[1]), level };
		if (rest === 'yesterday' || rest === 'today') return { kind: 'date', date: rest === 'today' ? todayKey : addDays(todayKey, -1), yearGiven: true, level };
		const date = parseDate(rest, todayKey);
		if (date) return { kind: 'date', ...date, level };
	}

	const filter = makeFilter();
	const words: QueryWord[] = [];
	const unknown: string[] = [];
	const add = (key: QueryWord['key'], value: string, source: string) => words.push({ key, value, source });
	for (let i = 0; i < tokens.length; i++) {
		const w = tokens[i]!;
		const nx = tokens[i + 1];
		const pair = nx ? `${w} ${nx}` : '';
		if (LEVEL_WORDS[w]) {
			const l = LEVEL_WORDS[w]!;
			if (!filter.levels.includes(l)) filter.levels.push(l);
			add('levels', l, w);
		} else if (pair === 'in progress' || pair === 'not started' || pair === 'not done' || pair === 'not finished') {
			const keys: StatusKey[] = pair === 'in progress' ? ['started'] : pair === 'not started' ? ['new'] : ['new', 'started'];
			for (const k of keys) if (!filter.status.includes(k)) filter.status.push(k);
			add('status', keys.join(','), pair);
			i++;
		} else if (STATUS_WORDS[w]) {
			const k = STATUS_WORDS[w]!;
			if (!filter.status.includes(k)) filter.status.push(k);
			add('status', k, w);
		} else if (SIZE_WORDS[w]) {
			filter.size = SIZE_WORDS[w]!;
			add('size', filter.size, w);
		} else if (/^\d{1,2}[x×]\d{1,2}$/.test(w)) {
			const [a, b] = w.split(/[x×]/).map(Number) as [number, number];
			filter.size = sizeKeyOf(Math.max(a, b));
			add('size', filter.size, w);
		} else if (WEEKDAYS[w] !== undefined) {
			filter.weekday = WEEKDAYS[w]!;
			add('weekday', String(filter.weekday), w);
		} else if (MONTHS[w] !== undefined) {
			const month = MONTHS[w]!;
			let year = latestMonth(month, todayKey);
			let source = w;
			if (nx && /^20\d\d$/.test(nx)) {
				year = Number(nx);
				source = pair;
				i++;
			}
			filter.month = { year, month };
			add('month', `${year}-${month}`, source);
		} else if ((w === 'last' || w === 'past') && (nx === '7' || nx === '30' || nx === '90') && tokens[i + 2] === 'days') {
			filter.range = nx;
			add('range', filter.range, `${pair} days`);
			i += 2;
		} else if ((w === 'last' || w === 'this' || w === 'past') && (nx === 'week' || nx === 'month' || nx === 'fortnight')) {
			filter.range = nx === 'month' ? '30' : '7';
			add('range', filter.range, pair);
			i++;
		} else if (SORT_WORDS[w]) {
			filter.sort = SORT_WORDS[w]!;
			add('sort', filter.sort, w);
		} else if (!FILL.has(w) && !unknown.includes(w)) {
			unknown.push(w);
		}
	}
	return { kind: 'words', filter, words, unknown };
}

const SORT_QUERY: Record<SortKey, string> = { new: '', old: 'oldest', quick: 'smallest', big: 'biggest' };
const WEEKDAY_QUERY = ['mondays', 'tuesdays', 'wednesdays', 'thursdays', 'fridays', 'saturdays', 'sundays'];
const STATUS_QUERY: Record<StatusKey, string> = { new: 'new', started: 'started', done: 'done', perfect: 'perfect', missed: 'missed' };

/**
 * The search words that read back as `f` ({@link parseQuery} gives the same filter):
 * how a stack's rules become a search ("Just show" in the stack builder).
 */
export function queryFromFilter(f: ArchiveFilter): string {
	const w: string[] = [...f.levels];
	const st = [...f.status].sort((a, b) => STATUS_KEYS.indexOf(a) - STATUS_KEYS.indexOf(b));
	if (st.length === 2 && st[0] === 'new' && st[1] === 'started') w.push('not done');
	else w.push(...st.map((k) => STATUS_QUERY[k]));
	if (f.size !== 'any') w.push(f.size === 'medium' ? '9x9' : f.size);
	if (f.weekday !== null) w.push(WEEKDAY_QUERY[f.weekday]!);
	if (f.month) w.push(`${MONTH_NAMES[f.month.month - 1]!.toLowerCase()} ${f.month.year}`);
	else if (f.range !== 'all') w.push(`last ${f.range} days`);
	if (SORT_QUERY[f.sort]) w.push(SORT_QUERY[f.sort]);
	return w.join(' ');
}

/** The query with one chip's words taken out (the first whole-word match). */
export function removeFromQuery(text: string, source: string): string {
	const esc = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '\\s+');
	return text
		.replace(new RegExp(`(^|\\s)${esc}(?=\\s|$)`, 'i'), ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

/** The query with every chip of `key` taken out (a no-results loosening). */
export function removeKeyFromQuery(text: string, words: readonly QueryWord[], key: FilterKey): string {
	return words.filter((w) => w.key === key).reduce((q, w) => removeFromQuery(q, w.source), text);
}

/** Where a number or a date lands. */
export type Lookup =
	| { kind: 'day'; day: ArchiveDay; level: Level | null }
	/** Today's: on Today, where it counts for the streak. */
	| { kind: 'today'; number: number | null }
	/** Not out yet: never a hint of what it is. */
	| { kind: 'future' }
	/** Before No. 1. */
	| { kind: 'before' }
	/** Out, but not in what's loaded (or the api didn't list it). */
	| { kind: 'missing' };

/**
 * Resolves a number or a date against the loaded days and the archive's bounds. A date
 * typed without a year means the latest one; if that's before No. 1, the coming one
 * (so "30 Oct" on 24 October 2026 isn't out yet).
 */
export function resolveLookup(
	q: Extract<ParsedQuery, { kind: 'number' | 'date' }>,
	days: readonly ArchiveDay[],
	earliest: ArchiveBound | null,
	todayKey: DateKey,
): Lookup {
	const todayNumber = earliest ? earliest.number + daysBetween(earliest.date, todayKey) : null;
	if (q.kind === 'number') {
		if (todayNumber !== null && q.number === todayNumber) return { kind: 'today', number: todayNumber };
		if (todayNumber !== null && q.number > todayNumber) return { kind: 'future' };
		if (earliest && q.number < earliest.number) return { kind: 'before' };
		const day = days.find((d) => d.number === q.number && d.date < todayKey);
		return day ? { kind: 'day', day, level: q.level } : { kind: 'missing' };
	}
	let date = q.date;
	if (earliest && date < earliest.date && !q.yearGiven) {
		const next = realDate(Number(date.slice(0, 4)) + 1, Number(date.slice(5, 7)), Number(date.slice(8, 10)));
		if (next && next > todayKey) return { kind: 'future' };
		if (next) date = next;
	}
	if (date === todayKey) return { kind: 'today', number: todayNumber };
	if (date > todayKey) return { kind: 'future' };
	if (earliest && date < earliest.date) return { kind: 'before' };
	const day = days.find((d) => d.date === date);
	return day ? { kind: 'day', day, level: q.level } : { kind: 'missing' };
}

/** The daily number of `dateKey`, counted from a known bound (numbers are one per day). */
export function numberForDate(dateKey: DateKey, bound: ArchiveBound): number {
	return bound.number + daysBetween(bound.date, dateKey);
}
